import { z } from "zod";

/**
 * Canonical task status values. This is the single source of truth for
 * the set of statuses — the rpc contract builds its zod enum from this
 * array, and @erp/shared-utils types the transition state machine against
 * the derived `TaskStatus` union. Add or remove a status here and both
 * sides follow.
 */
export const TASK_STATUSES = [
  "pending",
  "in_progress",
  "completed",
  "failed",
  "cancelled",
  "needs_input",
] as const;

/**
 * Statuses a person may snooze. `in_progress` and the terminal statuses are
 * excluded: snooze only hides a task that nobody is actively working and that
 * is still open. Parents and subtasks are independent — snoozing one does not
 * change the other.
 */
export const SNOOZABLE_TASK_STATUSES = ["pending", "needs_input"] as const;

export const TASK_SNOOZE_PRESETS = [
  "tomorrow",
  "three_days",
  "one_week",
  "specific",
] as const;

export const TASK_SNOOZE_REASONS = [
  "snooze",
  "clear",
  "assignee_changed",
  "needs_input",
  "task_edited",
] as const;

/** Returned when a snooze needs the caller to confirm a warning first. */
export const SNOOZE_CONFIRMATION_REQUIRED = "SNOOZE_CONFIRMATION_REQUIRED";

export type SnoozableTaskStatus = (typeof SNOOZABLE_TASK_STATUSES)[number];
export type TaskSnoozePreset = (typeof TASK_SNOOZE_PRESETS)[number];
export type TaskSnoozeReason = (typeof TASK_SNOOZE_REASONS)[number];

export type TaskStatus = (typeof TASK_STATUSES)[number];

/**
 * Terminal statuses — the task's lifecycle has concluded (though `completed`
 * and `cancelled` can still be reopened via the transition map). Entering one
 * of these is the only moment a task's `outcome` may be set.
 */
export const TERMINAL_TASK_STATUSES = [
  "completed",
  "failed",
  "cancelled",
] as const satisfies readonly TaskStatus[];

export type TerminalTaskStatus = (typeof TERMINAL_TASK_STATUSES)[number];

/**
 * Open (non-terminal, non-waiting-forever) statuses. Mirrors the WHERE clause
 * of the `core_tasks_open_anchor_unique` partial index, which allows at most
 * one open task per (organization, task type, anchor entity) — keep the two
 * in sync if statuses are ever added.
 */
export const OPEN_TASK_STATUSES = [
  "pending",
  "in_progress",
  "needs_input",
] as const satisfies readonly TaskStatus[];

export type OpenTaskStatus = (typeof OPEN_TASK_STATUSES)[number];

export function isTerminalTaskStatus(
  status: string,
): status is TerminalTaskStatus {
  return (TERMINAL_TASK_STATUSES as readonly string[]).includes(status);
}

/**
 * Outcome vocabulary for untyped tasks and for types that don't point
 * `outcomeEnumId` at a Core Enum. A task type replaces this by referencing a
 * Core Enum whose option names are the allowed outcomes.
 */
export const DEFAULT_TASK_OUTCOMES = ["success", "abandoned"] as const;

/**
 * How a typed task reaches `completed`:
 * - `explicit`: a human or workflow must close it, and closing is gated on
 *   every expected subtask (by type key) existing and being closed.
 * - `auto`: no gating; a later phase may add automatic completion when the
 *   last expected subtask closes.
 */
export const TASK_COMPLETION_MODES = ["explicit", "auto"] as const;

export type TaskCompletionMode = (typeof TASK_COMPLETION_MODES)[number];

/**
 * Concrete assignee kinds a task can point at. Mirrors the backend
 * `TaskAssigneeType` enum. A task is never assigned to a group — see
 * TASK_TYPE_DEFAULT_ASSIGNEE_TYPES for the type-level pool.
 */
export const TASK_ASSIGNEE_TYPES = ["user", "agent"] as const;

export type TaskAssigneeType = (typeof TASK_ASSIGNEE_TYPES)[number];

export const TaskAssigneeTypeSchema = z.enum(TASK_ASSIGNEE_TYPES);

/**
 * Default-assignee kinds a task type can declare. `user_group` is a pool:
 * one member is picked per created task by the type's allocation method,
 * and the task itself is assigned to that user. `workflow` names a reusable
 * workflow action: the task is created unassigned and, after commit, the
 * action runs with the task and returns the user to assign.
 */
export const TASK_TYPE_DEFAULT_ASSIGNEE_TYPES = [
  ...TASK_ASSIGNEE_TYPES,
  "user_group",
  "workflow",
] as const;

export type TaskTypeDefaultAssigneeType =
  (typeof TASK_TYPE_DEFAULT_ASSIGNEE_TYPES)[number];

export const TaskTypeDefaultAssigneeTypeSchema = z.enum(
  TASK_TYPE_DEFAULT_ASSIGNEE_TYPES,
);

/**
 * How a `user_group` default picks a member for each new task:
 * - `round_robin`: the member after the one most recently assigned a task of
 *   this type (stateless — derived from existing tasks).
 * - `least_open_tasks`: the member with the fewest open tasks org-wide,
 *   ties broken round-robin.
 */
export const TASK_TYPE_DEFAULT_ASSIGNEE_ALLOCATIONS = [
  "round_robin",
  "least_open_tasks",
] as const;

export type TaskTypeDefaultAssigneeAllocation =
  (typeof TASK_TYPE_DEFAULT_ASSIGNEE_ALLOCATIONS)[number];

export const TaskTypeDefaultAssigneeAllocationSchema = z.enum(
  TASK_TYPE_DEFAULT_ASSIGNEE_ALLOCATIONS,
);

/**
 * The optional default assignee configured on a task type, as sent on
 * create/update. `defaultAssigneeType` is the discriminator: when set, the
 * matching id must be present and the other ids must be null/omitted; when
 * null (clear) or omitted (unchanged), no id may be sent. The allocation is
 * required for `user_group` and must be null/omitted otherwise.
 */
export const TaskTypeDefaultAssigneeInputShape = {
  defaultAssigneeType: TaskTypeDefaultAssigneeTypeSchema.nullable().optional(),
  defaultAssigneeUserId: z.uuid().nullable().optional(),
  defaultAssigneeAgentDefinitionId: z.uuid().nullable().optional(),
  defaultAssigneeUserGroupId: z.uuid().nullable().optional(),
  defaultAssigneeWorkflowId: z.uuid().nullable().optional(),
  defaultAssigneeAllocation:
    TaskTypeDefaultAssigneeAllocationSchema.nullable().optional(),
} as const;

export type TaskTypeDefaultAssigneeInput = {
  defaultAssigneeType?: TaskTypeDefaultAssigneeType | null;
  defaultAssigneeUserId?: string | null;
  defaultAssigneeAgentDefinitionId?: string | null;
  defaultAssigneeUserGroupId?: string | null;
  defaultAssigneeWorkflowId?: string | null;
  defaultAssigneeAllocation?: TaskTypeDefaultAssigneeAllocation | null;
};

export type TaskTypeDefaultAssigneeIdField = keyof Pick<
  TaskTypeDefaultAssigneeInput,
  | "defaultAssigneeUserId"
  | "defaultAssigneeAgentDefinitionId"
  | "defaultAssigneeUserGroupId"
  | "defaultAssigneeWorkflowId"
>;

/** Which id column each default-assignee kind is stored in. */
export const TASK_TYPE_DEFAULT_ASSIGNEE_ID_FIELD: Record<
  TaskTypeDefaultAssigneeType,
  TaskTypeDefaultAssigneeIdField
> = {
  user: "defaultAssigneeUserId",
  agent: "defaultAssigneeAgentDefinitionId",
  user_group: "defaultAssigneeUserGroupId",
  workflow: "defaultAssigneeWorkflowId",
};

/**
 * Field names of one "typed target" on a task type: a discriminator, one id
 * column per kind, and an allocation that only applies to the `user_group`
 * pool kind. Both the default assignee and the escalation target follow this
 * shape, so the pairing refinement below is written once against it.
 */
interface TypedTargetFieldNames<Kind extends string> {
  typeField: string;
  idFieldByKind: Record<Kind, string>;
  allocationField: string;
  /** Human label for messages ("default assignee", "escalation target"). */
  label: string;
}

type TypedTargetValue = Record<string, unknown>;

function addTypedTargetIssue(
  ctx: z.RefinementCtx,
  path: string,
  message: string,
): void {
  ctx.addIssue({ code: "custom", path: [path], message });
}

/** The discriminator's id must be present and every other id absent. */
function refineTypedTargetIds<Kind extends string>(
  value: TypedTargetValue,
  ctx: z.RefinementCtx,
  kind: Kind | null,
  fields: TypedTargetFieldNames<Kind>,
): void {
  const requiredField = kind ? fields.idFieldByKind[kind] : null;
  if (requiredField && value[requiredField] == null) {
    addTypedTargetIssue(
      ctx,
      requiredField,
      `${requiredField} is required when ${fields.typeField} is '${kind}'`,
    );
  }
  const strayFields = Object.values<string>(fields.idFieldByKind).filter(
    (field) => field !== requiredField && value[field] != null,
  );
  for (const field of strayFields) {
    // With a kind, a stray id contradicts the discriminator. Without one
    // (cleared or unchanged), an id is a client bug, not a partial update.
    addTypedTargetIssue(
      ctx,
      requiredField ? field : fields.typeField,
      requiredField
        ? `${field} must be null when ${fields.typeField} is '${kind}'`
        : `${fields.typeField} is required when a ${fields.label} id is provided`,
    );
  }
}

/** The allocation is required for a pool and meaningless otherwise. */
function refineTypedTargetAllocation<Kind extends string>(
  value: TypedTargetValue,
  ctx: z.RefinementCtx,
  kind: Kind | null,
  fields: TypedTargetFieldNames<Kind>,
): void {
  const hasAllocation = value[fields.allocationField] != null;
  if (kind === "user_group" && !hasAllocation) {
    addTypedTargetIssue(
      ctx,
      fields.allocationField,
      `${fields.allocationField} is required when ${fields.typeField} is 'user_group'`,
    );
  } else if (kind !== "user_group" && hasAllocation) {
    addTypedTargetIssue(
      ctx,
      fields.allocationField,
      `${fields.allocationField} is only allowed when ${fields.typeField} is 'user_group'`,
    );
  }
}

/**
 * Build a superRefine body enforcing the typed-target pairing rules for one
 * set of field names. Shared by the create and update input schemas so the
 * two cannot drift.
 */
function typedTargetRefinement<Kind extends string>(
  fields: TypedTargetFieldNames<Kind>,
  isKind: (value: unknown) => value is Kind,
): (value: TypedTargetValue, ctx: z.RefinementCtx) => void {
  return (value, ctx) => {
    const raw = value[fields.typeField];
    const kind = isKind(raw) ? raw : null;
    refineTypedTargetIds(value, ctx, kind, fields);
    refineTypedTargetAllocation(value, ctx, kind, fields);
  };
}

function isDefaultAssigneeType(
  value: unknown,
): value is TaskTypeDefaultAssigneeType {
  return TaskTypeDefaultAssigneeTypeSchema.safeParse(value).success;
}

/**
 * superRefine body enforcing the default-assignee pairing rules. Shared by
 * the create and update input schemas so the two cannot drift.
 */
export const refineTaskTypeDefaultAssignee: (
  value: TaskTypeDefaultAssigneeInput,
  ctx: z.RefinementCtx,
) => void = typedTargetRefinement(
  {
    typeField: "defaultAssigneeType",
    idFieldByKind: TASK_TYPE_DEFAULT_ASSIGNEE_ID_FIELD,
    allocationField: "defaultAssigneeAllocation",
    label: "default assignee",
  },
  isDefaultAssigneeType,
);

// ---------------------------------------------------------------------------
// Escalation target (who receives an agent's `needs_input` hand-off)
// ---------------------------------------------------------------------------

/**
 * Escalation-target kinds a task type can declare. `needs_input` always hands
 * the task to a human, so — unlike the default assignee — an agent is not a
 * valid target. `user_group` is a pool: one member is picked per escalation
 * by the type's allocation method.
 */
export const TASK_TYPE_ESCALATION_TARGET_TYPES = [
  "user",
  "user_group",
] as const;

export type TaskTypeEscalationTargetType =
  (typeof TASK_TYPE_ESCALATION_TARGET_TYPES)[number];

export const TaskTypeEscalationTargetTypeSchema = z.enum(
  TASK_TYPE_ESCALATION_TARGET_TYPES,
);

/**
 * The optional escalation target configured on a task type, as sent on
 * create/update. Same pairing rules as the default assignee: the
 * discriminator selects which id must be present, the other must be absent,
 * and the allocation is required for (and only for) `user_group`.
 */
export const TaskTypeEscalationTargetInputShape = {
  escalationTargetType:
    TaskTypeEscalationTargetTypeSchema.nullable().optional(),
  escalationTargetUserId: z.uuid().nullable().optional(),
  escalationTargetUserGroupId: z.uuid().nullable().optional(),
  escalationTargetAllocation:
    TaskTypeDefaultAssigneeAllocationSchema.nullable().optional(),
} as const;

export type TaskTypeEscalationTargetInput = {
  escalationTargetType?: TaskTypeEscalationTargetType | null;
  escalationTargetUserId?: string | null;
  escalationTargetUserGroupId?: string | null;
  escalationTargetAllocation?: TaskTypeDefaultAssigneeAllocation | null;
};

export type TaskTypeEscalationTargetIdField = keyof Pick<
  TaskTypeEscalationTargetInput,
  "escalationTargetUserId" | "escalationTargetUserGroupId"
>;

/** Which id column each escalation-target kind is stored in. */
export const TASK_TYPE_ESCALATION_TARGET_ID_FIELD: Record<
  TaskTypeEscalationTargetType,
  TaskTypeEscalationTargetIdField
> = {
  user: "escalationTargetUserId",
  user_group: "escalationTargetUserGroupId",
};

function isEscalationTargetType(
  value: unknown,
): value is TaskTypeEscalationTargetType {
  return TaskTypeEscalationTargetTypeSchema.safeParse(value).success;
}

/** superRefine body enforcing the escalation-target pairing rules. */
export const refineTaskTypeEscalationTarget: (
  value: TaskTypeEscalationTargetInput,
  ctx: z.RefinementCtx,
) => void = typedTargetRefinement(
  {
    typeField: "escalationTargetType",
    idFieldByKind: TASK_TYPE_ESCALATION_TARGET_ID_FIELD,
    allocationField: "escalationTargetAllocation",
    label: "escalation target",
  },
  isEscalationTargetType,
);

/**
 * Who a checklist entry is designed to be worked by. `agent` covers all
 * automation (agent runs and workflow/system stages); `human` marks a
 * designed human step.
 */
export const TASK_INTENDED_ASSIGNEES = ["agent", "human"] as const;

export type TaskIntendedAssignee = (typeof TASK_INTENDED_ASSIGNEES)[number];

export const TaskIntendedAssigneeSchema = z.enum(TASK_INTENDED_ASSIGNEES);

export const TaskExpectedSubtaskSchema = z.object({
  key: z.string().min(1),
  intendedAssignee: TaskIntendedAssigneeSchema.optional(),
});

export type TaskExpectedSubtask = z.infer<typeof TaskExpectedSubtaskSchema>;

/**
 * A checklist entry as stored: either the compact bare type key or a rich
 * entry carrying the intended assignee.
 */
export const TaskExpectedSubtaskEntrySchema = z.union([
  z.string().min(1),
  TaskExpectedSubtaskSchema,
]);

export type TaskExpectedSubtaskEntry = z.infer<
  typeof TaskExpectedSubtaskEntrySchema
>;

// ---------------------------------------------------------------------------
// Task type actions (the `actions` JSONB column on core_task_types)
// ---------------------------------------------------------------------------

/** Any JSON-serializable value — used for declared action form schemas. */
export type JsonValue =
  string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

export const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JsonValueSchema),
    z.record(z.string(), JsonValueSchema),
  ]),
);

// ---------------------------------------------------------------------------
// Task action form schemas (the `formSchema` on a declared task action)
// ---------------------------------------------------------------------------

/**
 * Field types a task action form may declare. Deliberately small: a v1 action
 * form is a handful of typed inputs, not a form builder.
 */
export const TASK_ACTION_FORM_FIELD_TYPES = [
  "text",
  "textarea",
  "number",
  "boolean",
  "select",
  "date",
] as const;

export type TaskActionFormFieldType =
  (typeof TASK_ACTION_FORM_FIELD_TYPES)[number];

export const TaskActionFormFieldSchema = z
  .strictObject({
    /** Payload key the submitted value is stored under. */
    key: z.string().min(1),
    label: z.string().min(1),
    type: z.enum(TASK_ACTION_FORM_FIELD_TYPES),
    required: z.boolean().prefault(false),
    helpText: z.string().optional(),
    /** Allowed values for `select` fields. */
    options: z.array(z.string().min(1)).optional(),
  })
  .refine(
    (field) =>
      field.type !== "select" || (field.options && field.options.length > 0),
    { message: "Select fields must declare options" },
  );

export type TaskActionFormField = z.infer<typeof TaskActionFormFieldSchema>;

/**
 * The canonical shape of a declared action's `formSchema`: a flat list of
 * typed fields. Stored as plain JSON on the task type (`formSchema` stays
 * `JsonValue` on the action row so a hand-repaired type cannot brick reads);
 * writes, the admin editor, and executeAction all parse through here with
 * unknown keys rejected.
 */
export const TaskActionFormSchema = z
  .strictObject({
    fields: z.array(TaskActionFormFieldSchema),
  })
  .refine(
    (form) =>
      new Set(form.fields.map((f) => f.key)).size === form.fields.length,
    { message: "Form field keys must be unique" },
  );

/**
 * What invoking a declared task action does.
 *
 * `set_outcome` closes the task with a vocabulary outcome — the same write a
 * `complete_task` workflow node performs — so a button on the task type can
 * finish the work workflows are waiting on without a one-node workflow.
 */
export const TaskTypeActionEffectSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("emit_event"),
    // The system-event key emitted when the action is invoked.
    eventKey: z.string().min(1),
  }),
  z.object({
    kind: z.literal("start_workflow"),
    // The workflow template key started when the action is invoked.
    workflowTemplateKey: z.string().min(1),
  }),
  z
    .object({
      kind: z.literal("set_outcome"),
      status: z.enum(TERMINAL_TASK_STATUSES),
      /**
       * Outcome recorded on the task. Required when closing as completed or
       * failed. Omitted for cancellation, which records only a reason.
       */
      outcome: z.string().min(1).optional(),
      /**
       * Static explanation. A submitted form field named `outcomeReason`
       * overrides this at execution time.
       */
      outcomeReason: z.string().min(1).optional(),
    })
    .superRefine((effect, ctx) => {
      if (effect.status === "cancelled") {
        if (effect.outcome !== undefined) {
          ctx.addIssue({
            code: "custom",
            path: ["outcome"],
            message: "Cancellation does not record an outcome",
          });
        }
        return;
      }
      if (!effect.outcome) {
        ctx.addIssue({
          code: "custom",
          path: ["outcome"],
          message: "An outcome is required to close the task",
        });
      }
    }),
]);

export const TaskTypeActionSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  // Optional JSON-serializable form schema shown before invoking the action.
  formSchema: JsonValueSchema.optional(),
  effect: TaskTypeActionEffectSchema,
});

/**
 * The declared custom actions of a task type. Stored as-is in the
 * core_task_types.actions JSONB column; keys must be unique within a type.
 * Present formSchema values are parsed strictly so a typo like `requred`
 * cannot silently drop requiredness.
 */
export const TaskTypeActionsSchema = z
  .array(TaskTypeActionSchema)
  .refine(
    (actions) => new Set(actions.map((a) => a.key)).size === actions.length,
    { message: "Action keys must be unique" },
  )
  .superRefine((actions, ctx) => {
    for (const [index, action] of actions.entries()) {
      if (action.formSchema === undefined || action.formSchema === null) {
        continue;
      }
      const parsed = TaskActionFormSchema.safeParse(action.formSchema);
      if (!parsed.success) {
        ctx.addIssue({
          code: "custom",
          path: [index, "formSchema"],
          message:
            parsed.error.issues[0]?.message ?? "Invalid action form schema",
        });
      }
    }
  });

export type TaskTypeAction = z.infer<typeof TaskTypeActionSchema>;
export type TaskTypeActionEffect = z.infer<typeof TaskTypeActionEffectSchema>;

// ---------------------------------------------------------------------------
// Task playbooks (the `procedures` JSONB column on core_task_types + core_tasks)
// ---------------------------------------------------------------------------

/**
 * Which shelf a bound SOP sits on for the task:
 * - `required`: prescribed. Its title, phase or note, and replay/read
 *   instruction are delivered in the task briefing; the body remains behind
 *   `run_procedure` / `get_procedure`. Once an agent has worked the task it
 *   cannot complete until the agent engaged the procedure — a recorded
 *   successful replay or a procedure tool call — unless a human waives it.
 * - `suggested`: progressive disclosure. Only the title + `whenToUse` hint is
 *   shown; the agent pulls the body with `get_procedure` if the hint applies.
 * - `excluded`: instance-override only — switches a type-level binding off
 *   for one task. Rejected at type scope.
 */
export const TASK_PROCEDURE_MODES = [
  "required",
  "suggested",
  "excluded",
] as const;
export type TaskProcedureMode = (typeof TASK_PROCEDURE_MODES)[number];

/**
 * When a binding applies. `setup`/`teardown` run deterministically around the
 * agent's turn (task loop); `main` is left to the agent via the procedure
 * tools during its turn.
 */
export const TASK_PROCEDURE_PHASES = ["setup", "main", "teardown"] as const;
export type TaskProcedurePhase = (typeof TASK_PROCEDURE_PHASES)[number];

/**
 * Tiny path language for `parameterBindings` values. Anything that is not one
 * of these prefixes is a literal string.
 */
export const TASK_PROCEDURE_PARAM_METADATA_PREFIX = "$.task.metadata.";
export const TASK_PROCEDURE_PARAM_INPUT_PREFIX = "$.task.input.";
/**
 * Reads the key from `correction` when that object exists and the key is
 * present (including an explicit null). Otherwise falls through to
 * `$.task.input.<key>`. Resolved by `bindProcedureParameters`.
 */
export const TASK_PROCEDURE_PARAM_CORRECTION_PREFIX = "$.task.correction.";
export const TASK_PROCEDURE_PARAM_ANCHOR_ID = "$.task.anchorEntityId";
export const TASK_PROCEDURE_PARAM_ANCHOR_TYPE = "$.task.anchorEntityType";

export const TaskProcedureBindingSchema = z
  .object({
    sopId: z.uuid(),
    mode: z.enum(TASK_PROCEDURE_MODES),
    // Null = follow the SOP's published version; set = replay that snapshot.
    pinnedVersionId: z.uuid().nullable().default(null),
    phase: z.enum(TASK_PROCEDURE_PHASES).default("main"),
    // One line. For `suggested` this is the entire disclosure surface.
    whenToUse: z.string().trim().max(280).nullable().default(null),
    // SOP parameter name -> task path or literal (see TASK_PROCEDURE_PARAM_*).
    parameterBindings: z.record(z.string(), z.string()).default({}),
  })
  .superRefine((binding, ctx) => {
    if (binding.mode !== "required" && binding.phase !== "main") {
      ctx.addIssue({
        code: "custom",
        path: ["phase"],
        message: 'Only required procedures may use phase "setup" or "teardown"',
      });
    }
  });

export type TaskProcedureBinding = z.infer<typeof TaskProcedureBindingSchema>;
/** The write shape (defaults still optional), for service inputs. */
export type TaskProcedureBindingInput = z.input<
  typeof TaskProcedureBindingSchema
>;

/**
 * The bindings of one scope (a task type, or one task's overrides). Array
 * order is display + run order, like `expectedSubtaskTypeKeys`. Each SOP may
 * be bound once per scope.
 */
export const TaskProcedureBindingsSchema = z
  .array(TaskProcedureBindingSchema)
  .max(50)
  .refine(
    (bindings) =>
      new Set(bindings.map((b) => b.sopId)).size === bindings.length,
    { message: "Each SOP may be bound once per scope" },
  );

/**
 * Parse a declared action's raw `formSchema` into its field list. Returns
 * null when no schema is declared (`undefined`/`null`) — the action takes no
 * form. Throws nothing: an unparseable schema returns `{ ok: false }` so the
 * caller decides whether that's a render fallback or a hard error.
 */
export function parseTaskActionFormSchema(
  formSchema: unknown,
):
  | { ok: true; fields: TaskActionFormField[] | null }
  | { ok: false; error: string } {
  if (formSchema === undefined || formSchema === null) {
    return { ok: true, fields: null };
  }
  const parsed = TaskActionFormSchema.safeParse(formSchema);
  if (!parsed.success) {
    return { ok: false, error: parsed.error.message };
  }
  return { ok: true, fields: parsed.data.fields };
}

function taskActionFieldValueSchema(field: TaskActionFormField): z.ZodType {
  switch (field.type) {
    case "number":
      return z.number();
    case "boolean":
      return z.boolean();
    case "select":
      return z
        .string()
        .refine((value) => (field.options ?? []).includes(value), {
          message: `Must be one of: ${(field.options ?? []).join(", ")}`,
        });
    case "date":
      // Calendar date without a time component (yyyy-mm-dd).
      return z.iso.date();
    case "textarea":
    case "text":
      return field.required ? z.string().min(1) : z.string();
  }
}

/**
 * Build the runtime Zod validator for an action's submitted payload from its
 * declared field list. Strict: keys not declared by the form are rejected, so
 * an action payload can never smuggle undeclared data into the audit trail.
 */
export function buildTaskActionPayloadSchema(
  fields: readonly TaskActionFormField[],
): z.ZodType<Record<string, unknown>> {
  const shape: Record<string, z.ZodType> = {};
  for (const field of fields) {
    const value = taskActionFieldValueSchema(field);
    shape[field.key] = field.required ? value : value.optional();
  }
  return z.strictObject(shape);
}

/** Normalize compact and rich checklist entries to the rich shape. */
export function getExpectedSubtasks(
  entries: readonly TaskExpectedSubtaskEntry[] | null | undefined,
): TaskExpectedSubtask[] {
  return (entries ?? []).map((entry) =>
    typeof entry === "string" ? { key: entry } : entry,
  );
}

/** Return checklist type keys in declaration order. */
export function getExpectedSubtaskTypeKeys(
  entries: readonly TaskExpectedSubtaskEntry[] | null | undefined,
): string[] {
  return getExpectedSubtasks(entries).map((entry) => entry.key);
}

/**
 * The outcome vocabulary in force given the option names of the type's
 * outcome Core Enum. Pass null/undefined (or an empty list) for untyped
 * tasks and types without an outcome enum — the shared default applies.
 */
export function getAllowedTaskOutcomes(
  outcomeNames: readonly string[] | null | undefined,
): readonly string[] {
  return outcomeNames && outcomeNames.length > 0
    ? outcomeNames
    : DEFAULT_TASK_OUTCOMES;
}

// ---------------------------------------------------------------------------
// Agent ↔ human help exchange (core_task_actions action keys + payloads)
// ---------------------------------------------------------------------------

/**
 * Action key recorded (with an agent actor) when an agent escalates a task
 * to `needs_input` — the structured "ask" of the help conversation. These
 * keys are task-generic (any task type can carry a help exchange), so they
 * live here rather than in a domain consts module like the AP-specific
 * `accepted_invoice_matches`.
 */
export const AGENT_HELP_REQUESTED_ACTION_KEY = "agent_help_requested";

/**
 * Action key recorded (with a user actor) when a human answers an agent's
 * open help request via `tasks.provideInput` — the "response" side.
 */
export const HUMAN_INPUT_PROVIDED_ACTION_KEY = "human_input_provided";

/**
 * Payload of an `agent_help_requested` task action. Written by the
 * escalation paths; parsed by the loop's prompt injection, the open-request
 * endpoint, and the frontend callout. Lenient on optional fields so older
 * rows keep parsing as the shape grows.
 */
export const AgentHelpRequestPayloadSchema = z.object({
  /** What the agent needs the human to answer or decide. */
  question: z.string().min(1),
  /** Supporting context (e.g. the agent's escalation summary). */
  context: z.string().nullable().optional(),
  /** What the agent already tried before escalating. */
  attempted: z.string().nullable().optional(),
  /** The escalating agent, so provide-input can restore assignment. */
  agentDefinitionId: z.string().nullable().optional(),
});

export type AgentHelpRequestPayload = z.infer<
  typeof AgentHelpRequestPayloadSchema
>;

/**
 * Action key recorded when a task is handed back to a background agent. With
 * an AGENT actor (the chat agent) it closes the help exchange from the agent
 * side: the human's answer as the chat agent interpreted it, durable enough to
 * be replayed into the resumed agent's prompt rather than living only in a
 * comment. With a USER actor (`withoutAnswer`) it records that a person
 * returned a `needs_input` task without answering its open question.
 */
export const TASK_RETURNED_TO_AGENT_ACTION_KEY = "task_returned_to_agent";

/** Payload of a `task_returned_to_agent` task action. */
export const TaskReturnedToAgentPayloadSchema = z.object({
  /** Why the task is going back / what the agent should know. */
  note: z.string().min(1),
  /** The background agent the task was re-queued for. */
  targetAgentDefinitionId: z.string(),
  /** The help request this hand-back follows, or null when none was recorded. */
  helpRequestActionId: z.string().nullable().optional(),
  /**
   * A person re-queued the task from `needs_input` without answering (USER
   * actor). The help request is closed unanswered: the resumed agent goes on
   * without it rather than treating it as still open.
   */
  withoutAnswer: z.boolean().optional(),
});

export type TaskReturnedToAgentPayload = z.infer<
  typeof TaskReturnedToAgentPayloadSchema
>;

// ---------------------------------------------------------------------------
// Human takeover of a live operator run (core_task_actions action keys + payload)
// ---------------------------------------------------------------------------

/**
 * Action keys recorded (with a USER actor) when a person intervenes in a live operator run from
 * the agent monitor (AI-244): asked it to pause, sent it a typed instruction, or handed control
 * back after driving the desktop. Recorded on the core task the run works on behalf of, so the
 * task's history shows the intervention alongside the operator's own steps. Task-generic (any
 * task type can be worked by an operator), so they live here like the help-exchange keys.
 */
export const OPERATOR_PAUSE_REQUESTED_ACTION_KEY = "operator_pause_requested";
export const OPERATOR_INSTRUCTED_ACTION_KEY = "operator_instructed";
export const OPERATOR_CONTROL_RETURNED_ACTION_KEY = "operator_control_returned";

/** Payload of the three operator-takeover task actions. */
export const OperatorTakeoverActionPayloadSchema = z.object({
  /** The MONITORED agent run the person acted on (the takeover RPCs' input). */
  agentRunId: z.string(),
  /** The typed instruction (`operator_instructed` only). */
  instruction: z.string().nullable().optional(),
});

export type OperatorTakeoverActionPayload = z.infer<
  typeof OperatorTakeoverActionPayloadSchema
>;

/**
 * Action key recorded (with a user actor) when a human completes a task
 * whose playbook still has an unmet `required` procedure — the waiver. Its
 * frequency per (task type, SOP) is the "required but humans keep skipping
 * it" signal for playbook health.
 */
export const PLAYBOOK_WAIVED_ACTION_KEY = "playbook_waived";

export const PlaybookWaivedPayloadSchema = z.object({
  /** The required SOPs that were unmet at completion. */
  sopIds: z.array(z.uuid()).min(1),
  reason: z.string().min(1),
});

export type PlaybookWaivedPayload = z.infer<typeof PlaybookWaivedPayloadSchema>;

/**
 * Action key recorded (with a user actor) when a human releases the procedure
 * version a task locked on first activation, so the next attempt pins the
 * version the procedure currently resolves to.
 */
export const PROCEDURE_LOCK_RELEASED_ACTION_KEY = "procedure_lock_released";

export const ProcedureLockReleasedPayloadSchema = z.object({
  sopId: z.string(),
  /** Procedure name captured when the lock was taken. */
  name: z.string(),
  /** The locked version that was released. */
  releasedVersion: z.number().int().positive(),
});

export type ProcedureLockReleasedPayload = z.infer<
  typeof ProcedureLockReleasedPayloadSchema
>;

/** Payload of a `human_input_provided` task action. */
export const HumanInputProvidedPayloadSchema = z.object({
  /** The human's free-text answer. */
  response: z.string().min(1),
  /**
   * Id of the `agent_help_requested` core_task_actions row this answers,
   * or null when the task escalated before help requests were recorded.
   */
  helpRequestActionId: z.string().nullable().optional(),
});

export type HumanInputProvidedPayload = z.infer<
  typeof HumanInputProvidedPayloadSchema
>;

/**
 * How a planned procedure step is expected to run, read off the plan before replay:
 * `deterministic` replays captured code, `credential_fill` types a stored credential by reference,
 * `agentic` hands the authored instruction to the agent (no code), `branch` decides a path.
 */
export const ProcedurePlannedModeSchema = z.enum([
  "deterministic",
  "credential_fill",
  "agentic",
  "branch",
]);
export type ProcedurePlannedMode = z.infer<typeof ProcedurePlannedModeSchema>;

/** One planned step of a procedure snapshot. Every field past `index` is optional so snapshots
 * recorded before they existed still parse. */
export const ProcedureExecutionSnapshotStepSchema = z.object({
  id: z.string().nullable(),
  name: z.string(),
  index: z.number(),
  /** The authored instruction the step carries (what an agentic replay is told to do). */
  description: z.string().nullish(),
  /** RFC 2119 requirements carried from the authored procedure. */
  constraints: z.array(z.string()).nullish(),
  /** Parameter values resolved into this step's scope (redacted before persistence). */
  params: z.record(z.string(), z.string()).nullish(),
  plannedMode: ProcedurePlannedModeSchema.nullish(),
  /** The step may display a secret, so its replay screenshot is suppressed. */
  sensitive: z.boolean().nullish(),
  /** An absent target is a successful no-op rather than a failure. */
  bestEffort: z.boolean().nullish(),
  /** Branch steps only: the decision and the labelled paths it chooses between. */
  condition: z.string().nullish(),
  paths: z.array(z.object({ label: z.string() })).nullish(),
});
export type ProcedureExecutionSnapshotStep = z.infer<
  typeof ProcedureExecutionSnapshotStepSchema
>;

/** Immutable display evidence captured from the plan actually handed to an operator. */
export const ProcedureExecutionSnapshotSchema = z.object({
  sopId: z.string(),
  title: z.string(),
  version: z.number().nullable(),
  mode: z.string().nullable(),
  /** The root parameter values the run bound (redacted before persistence). */
  parameters: z.record(z.string(), z.string()).nullish(),
  steps: z.array(ProcedureExecutionSnapshotStepSchema),
});
export type ProcedureExecutionSnapshot = z.infer<
  typeof ProcedureExecutionSnapshotSchema
>;

/** Server-owned procedure snapshots for a logical task; browser sessions may serve many tasks. */
export const TaskProcedureSessionContextSchema = z.record(
  z.string(),
  z.object({
    version: z.number().int().positive(),
    name: z.string(),
    rules: z.array(z.string()),
    required: z.boolean(),
  }),
);
export type TaskProcedureSessionContext = z.infer<
  typeof TaskProcedureSessionContextSchema
>;
